// components/Shared/UnitDropdown.tsx
import React, { useRef, useEffect } from 'react';
import { UnitDropdownProps } from './UnitDropdown.types';

const UnitDropdown: React.FC<UnitDropdownProps> = ({
  className,
  units,
  activeUnitId,
  onChange,
  label = 'Unit of measurement:',
}) => {
  const [open, setOpen] = React.useState(false);
  const activeUnit = units.find((u) => u.id === activeUnitId)!;

  // ref for the list itself
  const listRef = useRef<HTMLUListElement>(null);

  // Close dropdown when clicking outside the <ul>
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // if open and click target is NOT inside the list
      if (open && listRef.current && !listRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={`mb-[2.4rem] flex items-center text-left ${className}`}>
      {label && <label className='mr-[0.5rem] text-[1.2rem]'>{label}</label>}

      <div className='relative'>
        <button
          type='button'
          onClick={() => setOpen((prev) => !prev)}
          className='flex w-full items-center justify-between rounded-[0.8rem] border-[0.1rem] border-primary p-[0.3rem] text-[1.2rem] font-medium text-primary'
        >
          <span className='ml-[0.333rem] mr-[1.6rem] whitespace-nowrap'>{activeUnit?.name}</span>
          <svg
            className={`h-[1.6rem] w-[1.6rem] transform transition-transform ${open ? 'rotate-180' : ''}`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </button>

        <ul
          ref={listRef}
          className={`shadow-prime absolute z-10 mt-[0.5rem] w-full origin-top transform rounded-[0.8rem] border-[0.1rem] border-primary bg-background-tertiary transition-all duration-200 ${
            open
              ? 'pointer-events-auto scale-100 opacity-100'
              : 'pointer-events-none scale-95 opacity-0'
          }`}
        >
          {units.map((u) => (
            <li
              key={u.id}
              className={`cursor-pointer px-[0.6rem] py-[0.4rem] text-[1.2rem] transition-colors duration-300 ease-in-out hover:font-medium hover:text-primary ${
                u.id === activeUnitId ? 'font-medium text-primary' : ''
              }`}
              onClick={() => {
                onChange(u.id);
                setOpen(false);
              }}
            >
              {u.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default UnitDropdown;
